import * as _ from 'lodash';
import { action, observable, runInAction } from 'mobx';
import { DefaultActionItem, IActionItem, IAssessment, IBranch, INote } from 'src/Definitions';
import { ActionItemDto, Client, IActionItemDto, NoteDto } from 'src/generated_client';
import AssessmentStore from '../AssessmentsStore/AssessmentsStore';
import DataTableStore from '../DataTableStore';
import GlobalStore from '../GlobalStore';
import NotificationStore from '../NotificationStore';

const ColumnSettingsDto = 'ActionItemDto';

export default class ActionItemsStore {
    @observable
    public isLoading: boolean = false;
    @observable
    public isModalOpen: boolean;
    @observable
    public hasFileChanged: boolean = false;
    @observable
    public actionItems: IActionItem[];
    @observable
    public selectedActionItem?: IActionItem;
    @observable
    public selectedRegions: string[] = [];
    @observable
    public selectedAssessments: IAssessment[] = [];
    @observable
    public selectedAssignees: string[] = [];
    @observable
    public selectedBranches: IBranch[] = [];
    @observable
    public completedDataTableStore: DataTableStore<IActionItemDto, IActionItem>;
    @observable
    public dueIn30DataTableStore: DataTableStore<IActionItemDto, IActionItem>;
    @observable
    public allRegionNames: string[] = [];
    @observable
    public allAssignees: string[] = [];
    @observable
    public assignees: string[] = [];
    @observable
    public filter: string = '';
    @observable
    public filterByDueIn30: boolean = false;
    @observable
    public filterByOverdue: boolean = false;
    @observable
    public filterByCompleted: boolean = false;
    @observable
    public showArchived: boolean = false;

    constructor(
        private assessmentStore: AssessmentStore,
        private globalStore: GlobalStore,
        private client: Client,
        private notificationStore: NotificationStore
    ) {
        this.dueIn30DataTableStore = new DataTableStore<IActionItemDto, IActionItem>(
            globalStore,
            ({ filter, page, orderBy, includeInactives }) =>
                client.getAllActionItems(
                    this.selectedBranches.map((x) => x.name).toString(),
                    this.selectedRegions.toString(),
                    this.assessmentStore.selectedAssessment
                        ? this.assessmentStore.selectedAssessment.name
                        : undefined,
                    undefined,
                    filter,
                    true,
                    false,
                    false,
                    false,
                    page,
                    5,
                    orderBy,
                    includeInactives
                ),
            (actionItem) => this.setupActionItems(actionItem),
            (userId, columns) =>
                client.apiColumnSettingsByUserIdByTypePut(userId, ColumnSettingsDto, columns)
        );

        this.completedDataTableStore = new DataTableStore<IActionItemDto, IActionItem>(
            globalStore,
            ({ filter, page, orderBy, includeInactives }) =>
                client.getAllActionItems(
                    this.selectedBranches.map((x) => x.name).toString(),
                    this.selectedRegions.toString(),
                    this.assessmentStore.selectedAssessment
                        ? this.assessmentStore.selectedAssessment.name
                        : undefined,
                    undefined,
                    filter,
                    false,
                    false,
                    true,
                    false,
                    page,
                    5,
                    orderBy,
                    includeInactives
                ),
            (actionItem) => this.setupActionItems(actionItem),
            (userId, columns) =>
                client.apiColumnSettingsByUserIdByTypePut(userId, ColumnSettingsDto, columns)
        );
    }

    @action.bound
    public sendNotificationEmail(actionItemId: string) {
        this.isLoading = true;
        return this.client
            .sendNotificationEmail(actionItemId)
            .then(() => {
                this.notificationStore.sendNotification(
                    '200',
                    'Notification email has been sent.',
                    { persist: false, variant: 'success' }
                );
            })
            .catch(() => {})
            .finally(() =>
                runInAction(() => {
                    this.isLoading = false;
                })
            );
    }

    @action.bound
    public setHasFileChanged(hasFileChanged: boolean) {
        this.hasFileChanged = hasFileChanged;
    }

    private loadDataTableData() {
        this.dueIn30DataTableStore.loadData();
        this.completedDataTableStore.loadData();
    }

    @action.bound
    public setSelectedRegions(regions: string[], loadTableData: boolean = false) {
        this.selectedRegions = regions;
        if (loadTableData) {
            this.loadDataTableData();
        } else {
            this.getAllActionItems();
        }
    }

    @action.bound
    public setSelectedBranches(branches: IBranch[], loadTableData: boolean = false) {
        this.selectedBranches = branches.filter((x) => x !== null);
        if (loadTableData) {
            this.loadDataTableData();
        } else {
            this.getAllActionItems();
        }
    }

    @action.bound
    public setSelectedAssessments(assessments: IAssessment[], shouldLoad: boolean = true) {
        this.selectedAssessments = assessments.filter((x) => x !== null);
        if (shouldLoad) {
            this.getAllActionItems();
        }
    }

    @action.bound
    public setSelectedAssignees(assignees: string[]) {
        this.selectedAssignees = assignees;
        this.getAllActionItems();
    }

    @action.bound
    public setFilteredAssignees(filter: string) {
        this.assignees = this.allAssignees.filter((a) =>
            a.toLocaleLowerCase().includes(filter.toLocaleLowerCase())
        );
    }

    @action.bound
    public closeDialog() {
        this.isModalOpen = false;
        this.selectedActionItem = undefined;
    }

    @action.bound
    public disableActionItem(actionItem: IActionItem) {
        this.isLoading = true;
        return this.client
            .archiveActionItem(actionItem.id)
            .then(() => this.getAllActionItems())
            .catch(() => {})
            .finally(() =>
                runInAction(() => {
                    this.isLoading = false;
                })
            );
    }

    @action.bound
    public onSearchChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.filter = event.target.value;
        this.getAllActionItems();
    };

    @action.bound
    public async getColumnSettingsAndActionItems() {
        if (!this.globalStore.user) {
            return;
        }
        this.isLoading = true;
        return this.client
            .apiColumnSettingsByUserIdByTypeGet(this.globalStore.user.id, ColumnSettingsDto)
            .then((data) => {
                runInAction(() => {
                    this.dueIn30DataTableStore.setColumns(data.result);
                    this.completedDataTableStore.setColumns(data.result);
                    const sortedColumn = _.find(
                        this.dueIn30DataTableStore.columns,
                        (col) => !!col.sort
                    );
                    const overDueSortedColumn = _.find(
                        this.completedDataTableStore.columns,
                        (col) => !!col.sort
                    );
                    if (sortedColumn) {
                        this.dueIn30DataTableStore.setTableOrderBy(sortedColumn.sort);
                    }
                    if (overDueSortedColumn) {
                        this.completedDataTableStore.setTableOrderBy(overDueSortedColumn.sort);
                    }

                    this.dueIn30DataTableStore.loadData();
                    this.completedDataTableStore.loadData();
                });
            })
            .catch(() => {})
            .finally(() => runInAction(() => (this.isLoading = false)));
    }

    @action.bound
    public updateFilters(
        filterByDueIn30: boolean = false,
        filterByOverdue: boolean = false,
        filterByCompleted: boolean = false,
        showArchived: boolean = false
    ) {
        this.filterByDueIn30 = filterByDueIn30;
        this.filterByOverdue = filterByOverdue;
        this.filterByCompleted = filterByCompleted;
        this.showArchived = showArchived;
        this.getAllActionItems(true, true);
    }

    @action.bound
    public getAllActionItems(
        shouldGetRegionNames: boolean = false,
        shouldGetAssignees: boolean = false
    ) {
        this.isLoading = true;
        const getAllActionItems = this.client.getAllActionItems(
            this.selectedBranches.map((branch) => branch.name).toString(),
            this.selectedRegions.toString(),
            this.selectedAssessments.map((assessment) => assessment.name).toString(),
            this.selectedAssignees.toString(),
            this.filter,
            this.filterByDueIn30,
            this.filterByOverdue,
            this.filterByCompleted,
            this.showArchived
        );

        const getAllRegionNames = shouldGetRegionNames
            ? this.client.getAllRegionNames().then((response) => {
                  runInAction(() => {
                      this.allRegionNames = response.result;
                  });
              })
            : Promise.resolve();

        return Promise.all([getAllActionItems, getAllRegionNames]).then((data) =>
            runInAction(() => {
                this.actionItems = this.setupActionItems(data[0].result);
                if (shouldGetAssignees) {
                    this.allAssignees = data[0].result.map(
                        (actionItem) => actionItem.assigneeEmail || ''
                    );
                }
                this.isLoading = false;
            })
        );
    }

    @action.bound
    public openDialogWithActionItem(actionItem: IActionItem) {
        this.isModalOpen = true;
        this.selectedActionItem = actionItem;
    }

    @action.bound
    public recoverActionItem(actionItem: IActionItem) {
        this.isLoading = true;
        return this.client
            .reactivateActionItem(actionItem.id)
            .then(() => this.getAllActionItems())
            .catch(() => {})
            .finally(() => {
                runInAction(() => {
                    this.isLoading = false;
                });
            });
    }

    private resetUIState() {
        this.getAllActionItems();
        this.closeDialog();
    }

    @action.bound
    public async saveActionItem(actionItem: IActionItem) {
        this.isLoading = true;
        let promise;
        const actionItemDto = new ActionItemDto(actionItem);
        if (actionItem.isNew) {
            promise = this.client.createActionItem(actionItemDto);
        } else {
            promise = this.client.updateActionItem(actionItem.id, actionItemDto);
        }
        return promise
            .then(() => {
                if (this.hasFileChanged) {
                    actionItem.actionItemAttachmentFiles.forEach((attachmentFile) => {
                        this.client
                            .uploadAttachmentFile(attachmentFile.attachmentFile, 'ActionItems')
                            .then(() => {})
                            .catch(() => {})
                            .finally(() =>
                                runInAction(() => {
                                    this.resetUIState();
                                })
                            );
                    });
                } else {
                    this.resetUIState();
                }
            })
            .catch(() => {})
            .finally(() =>
                runInAction(() => {
                    this.isLoading = false;
                })
            );
    }

    @action.bound
    public saveNote(note: INote) {
        this.isLoading = true;
        const noteDto = new NoteDto(note);
        return this.client
            .createNote(noteDto)
            .then(() => {})
            .finally(() =>
                runInAction(() => {
                    this.isLoading = false;
                })
            );
    }

    @action.bound
    public archiveNote(noteId: string) {
        this.isLoading = true;
        return this.client
            .archiveNote(noteId)
            .then(() => {})
            .finally(() =>
                runInAction(() => {
                    this.isLoading = false;
                })
            );
    }

    private setupActionItems = (actionItems: IActionItemDto[]) => {
        return actionItems.map((x) => this.setupActionItemMenuItems(this.addDefaultFields(x)));
    };

    private addDefaultFields = (actionItem: IActionItemDto): IActionItem => {
        return { ...DefaultActionItem(), ...actionItem };
    };

    private setupActionItemMenuItems = (actionItem: IActionItem) => {
        actionItem.menuItems = [];
        actionItem.menuItems.push({
            name: 'Edit',
            onClick: () => this.openDialogWithActionItem(actionItem),
        });
        if (actionItem.isActive) {
            actionItem.menuItems.push({
                isConfirm: true,
                name: 'Disable',
                onClick: () => this.disableActionItem(actionItem),
            });
        } else {
            actionItem.menuItems.push({
                isConfirm: true,
                name: 'Recover',
                onClick: () => this.recoverActionItem(actionItem),
            });
        }
        return actionItem;
    };
}



// WEBPACK FOOTER //
// ./src/stores/ActionItemsStore/ActionItemsStore.ts